import React, { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Customer } from "./types/customer";
import { CustomerType } from "./enums/customerType";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";

interface CustomerTableProps {
  customers: Customer[];
  title?: string;
  onEdit?: (customer: Customer) => void;
  onView?: (customer: Customer) => void;
}

type Order = "asc" | "desc";

const CustomerTable: React.FC<CustomerTableProps> = ({
  customers,
  title = "Customers",
  onEdit,
  onView
}) => {
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useKivunovaTranslation();

  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof Customer>("name");
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );

  const filteredList = useMemo(() => {
    if (!search.trim()) return customers;
    return customers.filter((customer) =>
      Object.values(customer)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [customers, search]);

  const sortedList = useMemo(() => {
    return [...filteredList].sort((a, b) => {
      const aValue = a[orderBy] ?? "";
      const bValue = b[orderBy] ?? "";
      if (typeof aValue === "number" && typeof bValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }
      return order === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [filteredList, order, orderBy]);

  const paginatedList = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedList.slice(start, start + itemsPerPage);
  }, [sortedList, page]);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(paginatedList.map((c) => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    );
  };

  const handleSort = (property: keyof Customer) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    customer: Customer
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedCustomer(customer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCustomer(null);
  };

  const handleEdit = () => {
    if (selectedCustomer && onEdit) {
      onEdit(selectedCustomer);
    }
    handleMenuClose();
  };

  const handleView = () => {
    if (selectedCustomer && onView) {
      onView(selectedCustomer);
    }
    handleMenuClose();
  };

  const columns = matchMD
    ? [
        {
          id: "name" as keyof Customer,
          label: t("customer-column-name", "Name")
        }
      ]
    : [
        {
          id: "name" as keyof Customer,
          label: t("customer-column-name", "Name")
        },
        {
          id: "phone" as keyof Customer,
          label: t("customer-column-phone", "Phone")
        },
        {
          id: "email" as keyof Customer,
          label: t("customer-column-email", "Email")
        },
        {
          id: "type" as keyof Customer,
          label: t("customer-column-type", "Type")
        },
        { id: "tin" as keyof Customer, label: t("customer-column-tin", "TIN") }
      ];

  return (
    <Box width="100%">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexDirection={matchMD ? "column" : "row"}
        gap={2}
      >
        <Typography variant="h6" fontWeight="bold">
          {title}
        </Typography>

        <TextField
          size="small"
          placeholder={t("customer-search-placeholder", "Search customers")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  checked={
                    selectedIds.length === paginatedList.length &&
                    paginatedList.length > 0
                  }
                  indeterminate={
                    selectedIds.length > 0 &&
                    selectedIds.length < paginatedList.length
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>
              {columns.map((column, id) => (
                <TableCell key={id}>
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={() => handleSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell align="right">
                {t("customer-column-actions", "Actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedList.map((customer, id) => (
              <TableRow key={id} hover>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    checked={selectedIds.includes(customer.id)}
                    onChange={() => handleSelectOne(customer.id)}
                  />
                </TableCell>
                <TableCell>{customer.name}</TableCell>
                {!matchMD && (
                  <>
                    <TableCell>{customer.phone}</TableCell>
                    <TableCell>{customer.email || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        label={customer.type}
                        size="small"
                        color={
                          customer.type === CustomerType.COMPANY
                            ? "primary"
                            : "default"
                        }
                      />
                    </TableCell>
                    <TableCell>{customer.tin || "-"}</TableCell>
                  </>
                )}
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, customer)}
                    sx={{
                      background:
                        anchorEl && selectedCustomer?.id === customer.id
                          ? theme.palette.grey[300]
                          : theme.palette.grey[100],
                      transition: "all 0.2s ease",
                      "&:hover": { backgroundColor: theme.palette.grey[300] }
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredList.length > itemsPerPage && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={Math.ceil(filteredList.length / itemsPerPage)}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
      >
        {onView && (
          <MenuItem onClick={handleView}>
            <ListItemIcon>
              <VisibilityOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("view", "View")}</ListItemText>
          </MenuItem>
        )}
        {onEdit && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("edit", "Edit")}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default CustomerTable;
